import { useEffect, useState } from 'react'
import type { JSX } from 'react'
import { fetchRun } from '../api.js'
import { RunState } from './status.jsx'
import type { RunSummary } from '../api.js'

const TERMINAL = new Set(['succeeded', 'failed'])

/**
 * Follows a run that was just started, until it stops.
 *
 * Polling *this* console's own API, which is a different thing from polling a
 * customer's cloud: the run record is updated by the completion event, so this
 * reads a value that is already there rather than reaching into someone else's
 * account. It stops as soon as the run is terminal, and on unmount.
 *
 * Three seconds because a plan finishes in a minute or two and an apply in an
 * hour; nobody is watching a progress line for an hour, and the deployment
 * screen shows the same state on reload.
 */
export const RunProgress = ({
  baseUrl,
  tenantId,
  run,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly run: RunSummary
}): JSX.Element => {
  const [latest, setLatest] = useState(run)

  useEffect(() => {
    setLatest(run)
    if (TERMINAL.has(run.state)) return

    let live = true
    const timer = setInterval(() => {
      void fetchRun(baseUrl, tenantId, run.runId)
        .then((answer) => {
          if (!live) return
          setLatest(answer)
          if (TERMINAL.has(answer.state)) clearInterval(timer)
        })
        // A failed poll is not a failed run. The record is authoritative and
        // the next tick will read it again.
        .catch(() => undefined)
    }, 3000)

    return () => {
      live = false
      clearInterval(timer)
    }
  }, [baseUrl, tenantId, run])

  return (
    <p className="run-progress" role="status">
      <code>{latest.runId}</code> {latest.command} — <RunState state={latest.state} />
      {latest.failure === undefined ? null : (
        <>
          {' — '}
          {latest.failure.detail}
        </>
      )}
    </p>
  )
}
